/*
  drawlz-pdp-rail.js — gives the PDP "Round it out" rail the same controls the
  homepage rails have: hover arrows + a slim progress bar, and the bar is
  DRAGGABLE.

  HISTORY, because it explains the shape of this file.
  v1 restyled the rail's NATIVE scrollbar to imitate the homepage. Wrong: the
  homepage bar is not a scrollbar, it is a <div> with a <span> thumb below the
  row, and the row's own scrollbar is hidden.
  v2 built that div — but hiding the native bar removed the only drag
  affordance the module had, and the injected div was purely an indicator. It
  looked right and did nothing. It also shipped without arrows, which the
  homepage has.
  v3 (this) keeps the homepage's look and puts the interaction back: the bar
  is a real control, and the arrows exist.

  WHERE THINGS GO. The markup is:

    .drawlz-recs                     <- module root, prog appended here
      .drawlz-recs__label
      .drawlz-scrollfade             <- already position:relative (it hosts
        .drawlz-recs__rail              the absolute fade edges), so it is the
        .drawlz-scrollfade__edge        natural host for the arrows
      (prog goes here, after the scrollfade)

  Nothing in snippets/drawlz-recommendations.liquid changes — every element
  here is injected, so the mobile DOM move (initRecBandMobile) carries the lot
  and drawlz-pdp.js keeps working untouched.

  Thumb maths and the nudge distance are lifted from assets/dzc-rail-nav.js so
  the two rails behave identically.

  v4 (Round 25, owner): the COLORWAY / variant swatch row gets the same bar.
  It had shipped with a restyled NATIVE scrollbar (6px, fog track, grey thumb),
  which read as a different component from every other slider on the site. Its
  native bar is now hidden in drawlz-pdp-type.css and it joins%TARGETS below.
  Bar only, no arrows: the arrows need a position:relative host, which the
  swatch row has no equivalent of, and at ~350px wide two 38px chevrons would
  cover the swatches they are meant to reveal.
*/
(function () {
  /* one entry per horizontal scroller that gets the homepage bar.
       arrows      also inject the hover chevrons (needs a relative host)
       matchWidth  size the bar to the row's CONTENT width, not the parent's
       stack       wrap row + bar in one block so the bar sits UNDER the row
       cls         extra class on the injected bar, for spacing tweaks */
  var TARGETS = [
    { sel: '.drawlz-recs__rail', label: 'Scroll recommendations', arrows: true,  matchWidth: false, stack: false, cls: '' },
    { sel: '.drawlz-swatches',   label: 'Scroll colors',          arrows: false, matchWidth: true,  stack: true,  cls: 'dzp-prog--sw' }
  ];

  function svgArrow(d) {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + d + '"/></svg>';
  }
  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function build(rail, cfg) {
    /* built-state is a JS EXPANDO, not a data attribute, on purpose:
       drawlz-pdp.js's initSwatchMirror CLONES the hero swatch row into the
       second close, and cloneNode copies attributes but NOT expandos — so the
       clone is correctly seen as unbuilt and gets a bar of its own. */
    if (!rail || rail.__dzpProg) return;
    rail.__dzpProg = true;

    /* the rec rail hangs its arrows on .drawlz-scrollfade (already relative,
       it carries the fade edges) and puts the bar after that wrapper. The
       swatch row has no wrapper, so the bar becomes the row's next sibling. */
    var scroller = cfg.arrows ? rail.parentNode : null;    /* .drawlz-scrollfade */
    var module = cfg.arrows ? ((scroller && scroller.parentNode) || scroller) : rail.parentNode;
    var anchor = cfg.arrows ? scroller : rail;
    if (!module || (cfg.arrows && !scroller)) return;

    /* The second close lays its buy row out as a horizontal flex, and the
       swatch mirror mount INSIDE it is another flex row — so a bare
       next-sibling bar became a third flex item and sat BESIDE the swatches
       instead of under them (owner report). Wrapping row + bar in one block
       makes the pair a single flex item, so the bar lands under the row in the
       hero and the second close alike, with no per-site layout rules. Safe to
       move the row: initSwatchMirror has already cloned by the time this runs,
       and every other consumer reaches the row by descendant selector or
       document-level delegation, so a wrapper is transparent to them. */
    if (cfg.stack) {
      var stack = document.createElement('div');
      stack.className = 'dzp-stack';
      module.insertBefore(stack, rail);
      stack.appendChild(rail);
      module = stack;
      anchor = rail;
    }

    /* ---------- arrows ---------- */
    var prev = null, next = null;
    if (cfg.arrows) {
      prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'dzp-nav dzp-nav--p';
      prev.setAttribute('aria-label', cfg.label + ' left');
      prev.innerHTML = svgArrow('M15 5l-7 7 7 7');

      next = document.createElement('button');
      next.type = 'button';
      next.className = 'dzp-nav dzp-nav--n';
      next.setAttribute('aria-label', cfg.label + ' right');
      next.innerHTML = svgArrow('M9 5l7 7-7 7');

      scroller.appendChild(prev);
      scroller.appendChild(next);
    }

    /* ---------- progress bar ----------
       appended AFTER the scrollfade wrapper, not inside it: the fade edges are
       absolutely positioned across that wrapper and would sit on top of the
       bar. */
    var prog = document.createElement('div');
    prog.className = cfg.cls ? 'dzp-prog ' + cfg.cls : 'dzp-prog';
    var thumb = document.createElement('span');
    prog.appendChild(thumb);
    if (anchor.nextSibling) module.insertBefore(prog, anchor.nextSibling);
    else module.appendChild(prog);

    /* ---------- state ---------- */
    function maxScroll() { return rail.scrollWidth - rail.clientWidth; }

    /* the bar spans the row's CONTENT width. The swatch row is capped at
       6.5 swatches (352px in the hero, narrower in the second close) while its
       parent group is the whole buy column, so a plain block child would
       overhang the swatches it belongs to.

       Deliberately NOT part of update(): update() runs on every scroll event,
       and getComputedStyle + clientWidth force a layout read, which would cost
       a reflow per frame for the whole of a drag. Width only changes on
       resize/load, so it is synced from refresh() on those paths alone.
       Writing only on change also stops the ResizeObserver feeding back. */
    var lastW = -1;
    function syncWidth() {
      if (!cfg.matchWidth) return;
      var cs = getComputedStyle(rail);
      var w = Math.round(rail.clientWidth
        - (parseFloat(cs.paddingLeft) || 0)
        - (parseFloat(cs.paddingRight) || 0));
      if (w > 0 && w !== lastW) { lastW = w; prog.style.width = w + 'px'; }
    }

    function update() {
      var max = maxScroll();
      var scrollable = max > 2;

      /* nothing to scroll = no controls, same rule as the homepage. Never show
         a dead arrow or a full-width dead track. For the swatch row this is
         what preserves the old behaviour of six-or-fewer colorways showing no
         slider at all. */
      if (scroller) scroller.classList.toggle('dzp-has-nav', scrollable);
      prog.classList.toggle('is-live', scrollable);
      if (!scrollable) { thumb.style.width = '100%'; thumb.style.transform = ''; return; }

      var x = rail.scrollLeft;
      if (prev) prev.classList.toggle('is-off', x <= 1);
      if (next) next.classList.toggle('is-off', x >= max - 1);

      var ratio = rail.clientWidth / rail.scrollWidth;
      var pos = x / max;
      thumb.style.width = (ratio * 100).toFixed(3) + '%';
      thumb.style.transform = 'translateX(' + (pos * (100 / ratio - 100)).toFixed(3) + '%)';
    }

    /* ---------- arrow nudge ---------- */
    function nudge(dir) {
      rail.scrollBy({
        left: dir * Math.max(160, rail.clientWidth * 0.8),
        behavior: reduced() ? 'auto' : 'smooth'
      });
    }
    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });

    /* ---------- drag the bar ----------
       Grab the thumb and it tracks the cursor from wherever it was grabbed;
       click anywhere else on the track and the thumb jumps to centre under the
       cursor, then keeps following. Mapping is thumb-travel -> scroll-range, so
       the cursor and the cards stay in step for the whole drag. */
    var grabOffset = 0, travel = 0, max = 0, dragging = false;

    prog.addEventListener('pointerdown', function (e) {
      max = maxScroll();
      if (max <= 2) return;

      var pr = prog.getBoundingClientRect();
      var tr = thumb.getBoundingClientRect();
      travel = pr.width - tr.width;
      if (travel <= 0) return;

      var onThumb = e.clientX >= tr.left && e.clientX <= tr.right;
      grabOffset = onThumb ? (e.clientX - tr.left) : (tr.width / 2);

      dragging = true;
      prog.classList.add('is-drag');
      /* capture so the drag survives the cursor leaving the 18px strip */
      if (prog.setPointerCapture) prog.setPointerCapture(e.pointerId);
      e.preventDefault();
      moveTo(e.clientX, pr.left);
    });

    function moveTo(clientX, progLeft) {
      var x = clientX - progLeft - grabOffset;
      if (x < 0) x = 0;
      if (x > travel) x = travel;
      rail.scrollLeft = (x / travel) * max;
    }

    prog.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      moveTo(e.clientX, prog.getBoundingClientRect().left);
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      prog.classList.remove('is-drag');
      if (prog.releasePointerCapture && e && e.pointerId != null) {
        try { prog.releasePointerCapture(e.pointerId); } catch (err) {}
      }
    }
    prog.addEventListener('pointerup', endDrag);
    prog.addEventListener('pointercancel', endDrag);

    /* keyboard: the bar is a real control, so give it arrow keys */
    prog.tabIndex = 0;
    prog.setAttribute('role', 'scrollbar');
    prog.setAttribute('aria-label', cfg.label);
    prog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { nudge(-1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { nudge(1); e.preventDefault(); }
    });

    rail.addEventListener('scroll', update, { passive: true });

    /* the rail is filled CLIENT-SIDE (recDynFill adds Last Chance and fallback
       candidates after boot) and the tiles are percentage-width, so scrollWidth
       changes well after DOMContentLoaded. The swatch row is server-rendered
       but its cap is in custom properties that differ per breakpoint, so it
       wants the same observer. */
    function refresh() { syncWidth(); update(); }
    if (window.ResizeObserver) new ResizeObserver(refresh).observe(rail);
    else window.addEventListener('resize', refresh);
    window.addEventListener('load', refresh);

    refresh();
  }

  function scan() {
    for (var t = 0; t < TARGETS.length; t++) {
      var cfg = TARGETS[t];
      var els = document.querySelectorAll(cfg.sel);
      for (var i = 0; i < els.length; i++) build(els[i], cfg);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  /* build() is idempotent; re-scan once things settle because the band is
     relocated on mobile and can be re-rendered by the quick-add flow. */
  window.addEventListener('load', scan);
})();
